import { onScopeDispose, ref, toValue, watch, type MaybeRefOrGetter, type Ref } from 'vue'
import { api } from '@/services/api'
import { resolveMediaUrl } from '@/utils/media'

const PRIVATE_UPLOAD = /^\/(?:api\/v1\/)?uploads\/private\/([^/]+)$/
/** Refresh a presigned link this long before it expires. */
const REFRESH_MARGIN_MS = 60_000

interface CachedLink {
  url: string
  expiresAt: number
}

const cache = new Map<string, CachedLink>()
const inflight = new Map<string, Promise<CachedLink>>()

export function privateUploadName(url?: string | null): string | null {
  return url?.match(PRIVATE_UPLOAD)?.[1] ?? null
}

async function fetchLink(name: string): Promise<CachedLink> {
  const cached = cache.get(name)
  if (cached && cached.expiresAt - REFRESH_MARGIN_MS > Date.now()) return cached

  let pending = inflight.get(name)
  if (!pending) {
    pending = api
      .getPrivateUploadLink(name)
      .then(({ url, expiresAt }) => {
        const link = { url, expiresAt: Date.parse(expiresAt) }
        cache.set(name, link)
        return link
      })
      .finally(() => inflight.delete(name))
    inflight.set(name, pending)
  }
  return pending
}

/**
 * Turns a stored media path into something an `<img>` or `<a>` can use.
 *
 * Public uploads resolve immediately. Private uploads (`/uploads/private/...`)
 * can't be fetched with the Bearer token an `<img>` never sends, so this asks the
 * API for a short-lived presigned link and renews it before it expires. The ref
 * stays empty while loading, or when the file is missing or not ours to see.
 */
export function useMediaUrl(source: MaybeRefOrGetter<string | null | undefined>): Ref<string> {
  const resolved = ref('')
  let refreshTimer: ReturnType<typeof setTimeout> | undefined
  let generation = 0

  async function resolve(url: string | null | undefined) {
    clearTimeout(refreshTimer)
    const current = ++generation
    const name = privateUploadName(url)
    if (!name) {
      resolved.value = resolveMediaUrl(url)
      return
    }

    resolved.value = ''
    try {
      const link = await fetchLink(name)
      if (current !== generation) return
      resolved.value = link.url
      const renewIn = Math.max(link.expiresAt - REFRESH_MARGIN_MS - Date.now(), REFRESH_MARGIN_MS)
      refreshTimer = setTimeout(() => resolve(url), renewIn)
    } catch {
      if (current === generation) resolved.value = ''
    }
  }

  watch(() => toValue(source), resolve, { immediate: true })
  onScopeDispose(() => clearTimeout(refreshTimer))
  return resolved
}
